import { useEffect, useRef, useState } from 'react';
import { produce } from 'immer';
import { useHistory } from 'react-router-dom';

import {
  type ExperimentVariants,
  useExperiment,
} from '@hungryroot/experiments';
import { type OrderV3 } from '@hungryroot/rest';
import { REACTIVATE_ROUTE } from '@hungryroot/routes';

export enum OverlayTypes {
  None = 'None',
  Empty = 'Empty',
  Reactivate = 'Reactivate',
  CartIsReady = 'CartIsReady',
  Loading = 'Loading',
}

interface OverlayStateEmpty {
  type: OverlayTypes.Empty;
}

interface OverlayStateCartIsReady {
  type: OverlayTypes.CartIsReady;
}

interface OverlayStateReactivate {
  type: OverlayTypes.Reactivate;
}

interface OverlayStateNone {
  type: OverlayTypes.None;
}

interface OverlayStateLoading {
  type: OverlayTypes.Loading;
}

export type OverlayState =
  | OverlayStateEmpty
  | OverlayStateReactivate
  | OverlayStateCartIsReady
  | OverlayStateNone
  | OverlayStateLoading;

export function getShowCart(params: {
  overlayState: OverlayState;
  isDesktop: boolean;
  redesignedMhrVariant?: ExperimentVariants<171>;
  discoveryHomeVariant?: ExperimentVariants<188>;
  cartActive: boolean;
}): boolean {
  const {
    overlayState,
    isDesktop,
    redesignedMhrVariant,
    discoveryHomeVariant,
    cartActive,
  } = params;
  if (
    redesignedMhrVariant === undefined ||
    discoveryHomeVariant === undefined
  ) {
    return false;
  }

  // If no overlay, just use the plan state value
  if (overlayState.type === OverlayTypes.None) {
    return cartActive;
  }

  // Don't show the cart for any of the overlays that are disabled
  // with experiment 171 or experiment 188.
  if (
    overlayState.type === OverlayTypes.Reactivate &&
    (redesignedMhrVariant > 0 || discoveryHomeVariant > 0)
  ) {
    return false;
  }

  // We have some kind of overlay
  // and we are on desktop,
  // show the cart
  if (isDesktop === true) {
    return true;
  }

  // We have some kind of overlay, but
  // we are on mobile
  // do not show the cart
  return false;
}

export function getShowCartPreview(params: {
  overlayState: OverlayState;
  isDesktop: boolean;
}): boolean {
  const { overlayState, isDesktop } = params;
  // If no overlay, don't show the preview
  if (overlayState.type === OverlayTypes.None) {
    return false;
  }

  // We have some kind of overlay
  // but we are on desktop,
  // do not show the preview
  if (isDesktop === true) {
    return false;
  }

  // We have some kind of overlay, and
  // we are on mobile.
  // Show the preview
  return true;
}

function getInitialOverlayState(params: {
  urlIsReactivated: boolean;
  discoveryHomeVariant?: ExperimentVariants<188>;
}): OverlayState {
  const { urlIsReactivated, discoveryHomeVariant } = params;

  // Don't show CartIsReady overlay for users in experiment 188
  if (discoveryHomeVariant !== undefined && discoveryHomeVariant > 0) {
    return {
      type: OverlayTypes.None,
    };
  }

  if (urlIsReactivated === true) {
    if (discoveryHomeVariant === undefined) {
      return {
        type: OverlayTypes.None,
      };
    }

    if (discoveryHomeVariant < 1) {
      return {
        type: OverlayTypes.Reactivate,
      };
    }
  }

  return {
    type: OverlayTypes.None,
  };
}

interface Params {
  orderInCart: Pick<OrderV3, 'id' | 'sequence'> | undefined;
  latestSeen: number | undefined;
}

export interface UseOverlaysResult {
  dismissOverlay: () => void;
  showOverlay(overlayState: OverlayState): void;
  overlayState: OverlayState;
}

export function useOverlays(params: Params): UseOverlaysResult {
  const redesignedMhrVariant = useExperiment(171);
  const discoveryHomeVariant = useExperiment(188);
  const initialOverlayStateSetRef = useRef(false);
  const history = useHistory<{ autoRedirect?: true } | undefined>();
  const { orderInCart, latestSeen } = params;
  const { location } = history;
  const { search: historySearch } = location;
  const search = new URLSearchParams(historySearch);
  const urlIsReactivated = location.pathname === REACTIVATE_ROUTE;
  const initialState = getInitialOverlayState({
    urlIsReactivated,

    discoveryHomeVariant,
  });

  const [overlayState, setOverlayState] = useState({
    current: initialState,
    initial: initialState,
    cartIsReadyShown: false,
  });

  const [currentLocationState, setCurrentLocationState] = useState(location);
  if (currentLocationState !== location) {
    if (location.state?.autoRedirect !== true) {
      setOverlayState(
        produce((draft) => {
          draft.current = {
            type: OverlayTypes.None,
          };
        })
      );
    }
    setCurrentLocationState(location);
  }

  if (
    discoveryHomeVariant !== undefined &&
    initialOverlayStateSetRef.current === false
  ) {
    initialOverlayStateSetRef.current = true;

    setOverlayState({
      ...overlayState,
      current: initialState,
      initial: initialState,
    });
  }

  // If not welcome, check for reactivate
  useEffect(() => {
    if (urlIsReactivated === false) {
      return;
    }

    const next = new URLSearchParams(search);
    next.delete('reactivate');
    history.push({
      search: next.toString(),
      // This needs to be here
      // because we have logic to handle when a user navigates
      // This redirect will trigger a URL change,
      // so we need to signal that the redirect was
      // not a user interaction
      state: {
        autoRedirect: true,
      },
    });
  }, [urlIsReactivated]);

  if (
    orderInCart !== undefined &&
    (latestSeen === undefined || latestSeen < orderInCart.id) &&
    orderInCart.sequence > 1 &&
    redesignedMhrVariant !== undefined &&
    redesignedMhrVariant < 1 &&
    discoveryHomeVariant !== undefined &&
    discoveryHomeVariant < 1 &&
    overlayState.initial.type === OverlayTypes.None &&
    overlayState.cartIsReadyShown === false
  ) {
    setOverlayState(
      produce((draft) => {
        draft.cartIsReadyShown = true;
        draft.current = {
          type: OverlayTypes.CartIsReady,
        };
      })
    );
  }

  return {
    dismissOverlay() {
      setOverlayState(
        produce((draft) => {
          draft.current = {
            type: OverlayTypes.None,
          };
        })
      );
    },
    showOverlay(nextOverlay: OverlayState) {
      setOverlayState(
        produce((draft) => {
          draft.current = nextOverlay;
        })
      );
    },
    overlayState: overlayState.current,
  };
}
